import {
	addDays,
	addMonths,
	addYears,
	endOfDay,
	endOfMonth,
	endOfYear,
	startOfDay,
	startOfMonth,
	startOfYear,
	subDays,
	subMonths,
	subYears,
} from 'date-fns';

/**
 * Stable, human readable values for the "Date added" filter. These are the
 * values surfaced in the filter and persisted to the URL, so they are
 * deliberately readable and stable — the concrete before/after date range each
 * one expands to is derived from "now" and is an implementation detail.
 */
export const attachmentDateFilters = [
	'today',
	'yesterday',
	'last7Days',
	'last30Days',
	'thisMonth',
	'thisYear',
	'lastYear',
] as const;

export type AttachmentDateFilterValue = (typeof attachmentDateFilters)[number];

/**
 * An inclusive-exclusive date range. The backend attachment search filters
 * exclusive of the dates provided, so the before/after dates are set to the day
 * before and day after the desired range.
 */
export type AttachmentDateRange = {
	after: Date;
	before: Date;
};

/**
 * Type guard for values coming from untrusted sources such as the URL.
 */
export const isAttachmentDateFilterValue = (value: unknown): value is AttachmentDateFilterValue =>
	attachmentDateFilters.some((dateValue) => dateValue === value);

/**
 * Expands a "Date added" filter value into the concrete before/after date range
 * the backend attachment search expects, relative to `now`. Kept pure (no i18n)
 * so it can be reused both in the filter UI and when seeding the preloaded query
 * from the URL in the entry point.
 */
export const getAttachmentDateFilterRange = (
	value: AttachmentDateFilterValue,
	now: Date = new Date(),
): AttachmentDateRange => {
	switch (value) {
		case 'today':
			return {
				after: endOfDay(subDays(now, 1)),
				before: startOfDay(addDays(now, 1)),
			};
		case 'yesterday':
			return {
				after: endOfDay(subDays(now, 2)),
				before: startOfDay(subDays(now, 1)),
			};
		case 'last7Days':
			return {
				after: endOfDay(subDays(now, 8)),
				before: startOfDay(addDays(now, 1)),
			};
		case 'last30Days':
			return {
				after: endOfDay(subDays(now, 31)),
				before: startOfDay(addDays(now, 1)),
			};
		case 'thisMonth':
			return {
				after: endOfDay(endOfMonth(subMonths(now, 1))),
				before: startOfDay(startOfMonth(addMonths(now, 1))),
			};
		case 'thisYear':
			return {
				after: endOfDay(endOfYear(subYears(now, 1))),
				before: startOfDay(startOfYear(addYears(now, 1))),
			};
		case 'lastYear':
			return {
				after: endOfDay(endOfYear(subYears(now, 2))),
				before: startOfDay(startOfYear(now)),
			};
		default: {
			// Exhaustiveness guard: every known value must be handled above.
			const _exhaustive: never = value;
			throw new Error(`Unhandled attachment date filter value: ${String(_exhaustive)}`);
		}
	}
};
